import { useEffect, useRef } from "react";
import type { MediaDTO } from "@wedding/shared-types";
import { MediaCard } from "./MediaCard.js";

interface MediaGridProps {
  items: MediaDTO[];
  onOpen: (index: number) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  selectionMode?: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (id: string) => void;
}

export function MediaGrid({
  items,
  onOpen,
  onLoadMore,
  hasMore,
  loadingMore,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
}: MediaGridProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { rootMargin: "600px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  if (items.length === 0) {
    return (
      <div className="card" style={{ textAlign: "center" }}>
        <p>Aucune photo ou vidéo pour le moment.</p>
      </div>
    );
  }

  return (
    <div className="stack">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: "var(--space-2)",
        }}
      >
        {items.map((media, index) => (
          <MediaCard
            key={media.id}
            media={media}
            onOpen={() => onOpen(index)}
            selectionMode={selectionMode}
            selected={selectedIds?.has(media.id)}
            onToggleSelect={() => onToggleSelect?.(media.id)}
          />
        ))}
      </div>
      {hasMore && (
        <div
          ref={sentinelRef}
          style={{ display: "flex", justifyContent: "center", padding: "var(--space-4)" }}
        >
          <button type="button" className="btn btn-secondary" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? "Chargement…" : "Charger plus"}
          </button>
        </div>
      )}
    </div>
  );
}
